/* Keep purchase shortcuts beside the picker, and land shopping links at its heading. */
(() => {
  'use strict';
  const mounted = new WeakMap();
  const HEADER = '.shopify-section-header-sticky, sticky-header, header[role="banner"], #shopify-section-header, .section-header';
  const OVERLAY = 'dialog[open], [aria-modal="true"], [role="dialog"]:not([aria-hidden="true"])';
  const OVERLAY_CANDIDATE = 'dialog, [aria-modal], [role="dialog"]';
  const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);
  const GAP = 16;
  const CSS = `
    :host([data-picker-navigation]) #mobile-commit {
      display:none!important;visibility:hidden!important;pointer-events:none!important;
    }
    :host([data-picker-navigation]) #mobile-commit[data-commit-visible="true"] {
      display:flex!important;visibility:visible!important;pointer-events:auto!important;
      position:fixed!important;top:auto!important;right:auto!important;
      left:var(--steadi-commit-left)!important;width:var(--steadi-commit-width)!important;
      bottom:var(--steadi-commit-bottom)!important;transform:none!important;
      margin:0!important;box-sizing:border-box!important;z-index:10!important;
      align-items:center;background:#fff;border:1px solid #d7dfd6;
      box-shadow:0 5px 30px #142b291c;
    }
    :host([data-picker-navigation]) #mobile-commit[data-commit-layout="desktop"] {
      border-radius:12px;padding:12px 15px;gap:22px;
    }
    :host([data-picker-navigation]) #mobile-commit[data-commit-layout="desktop"]>div {flex:1;min-width:0}
    :host([data-picker-navigation]) #mobile-commit[data-commit-layout="desktop"] b {font-size:15px;display:block;white-space:nowrap}
    :host([data-picker-navigation]) #mobile-commit[data-commit-layout="desktop"]>div>span {font-size:12px;display:block;margin-top:5px;color:var(--muted)}
    :host([data-picker-navigation]) #mobile-commit[data-commit-layout="desktop"] .add-button {width:200px;font-size:15px;padding:15px 18px;min-height:48px}
    :host([data-picker-navigation]) #mobile-commit[data-commit-layout="mobile"] {
      border-radius:0;border-inline:0;padding:6px 16px calc(6px + env(safe-area-inset-bottom));
      gap:12px;box-shadow:0 -8px 18px #152a2708;
    }
    :host([data-picker-navigation]) #mobile-commit[data-commit-layout="mobile"]>div {flex:1;min-width:0}
    :host([data-picker-navigation]) #mobile-commit[data-commit-layout="mobile"] b {font-size:14px;display:block;white-space:nowrap}
    :host([data-picker-navigation]) #mobile-commit[data-commit-layout="mobile"]>div>span {display:none}
    :host([data-picker-navigation]) #mobile-commit[data-commit-layout="mobile"] .edit-pack {min-height:44px;gap:6px}
    :host([data-picker-navigation]) #mobile-commit[data-commit-layout="mobile"] .add-button {width:auto;min-height:44px;min-width:148px;padding:10px 14px;font-size:14px;gap:8px}
    @media(max-width:350px){
      :host([data-picker-navigation]) #mobile-commit[data-commit-layout="mobile"] {padding-inline:12px;gap:8px}
      :host([data-picker-navigation]) #mobile-commit[data-commit-layout="mobile"] .add-button {min-width:132px}
    }
  `;
  const attr = (el, name, value) => {
    const next = String(value);
    if (el.getAttribute(name) !== next) el.setAttribute(name, next);
  };
  const visible = el => {
    if (!el || el.hidden || el.hasAttribute?.('inert')) return false;
    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);
    return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  };
  const editable = el => !!el && (el.isContentEditable || el.matches?.('textarea, select, input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="hidden"])'));

  function mount({ host, root }) {
    if (!host || !root) return null;
    if (mounted.has(host)) return mounted.get(host);
    const bar = root.querySelector('#mobile-commit');
    const primary = root.querySelector('#add-to-cart');
    const picker = root.querySelector('#picker');
    const heading = root.querySelector('.product-heading');
    const choices = root.querySelector('.color-section');
    if (!bar || !primary || !picker || !heading || !choices) return null;
    const style = document.createElement('style');
    style.setAttribute('data-steadi-picker-navigation', '');
    style.textContent = CSS;
    root.append(style);
    // Container queries on the photo surface create a fixed-position containing
    // block. Move this same node outside it; its selection and click handlers stay.
    const originalParent = bar.parentNode, originalNext = bar.nextSibling;
    root.append(bar);
    host.setAttribute('data-picker-navigation', 'ready');
    const cleanups = [];
    let frame = 0, destroyed = false, reachedChoices = false, barHeight = 76;
    let settleUntil = 0, settleAfter = 0, settleTimer = 0, pendingFrame = 0;
    let position = '';
    let pickerIntersects = null;
    // Keep this list current when sections are inserted, rather than searching the
    // whole document again on every scroll frame.
    let headers = [...document.querySelectorAll(HEADER)];
    let overlays = [];
    const collectOverlays = () => { overlays = [document, root].flatMap(scope => [...scope.querySelectorAll(OVERLAY_CANDIDATE)]); };
    collectOverlays();
    let scrollLock = `${document.documentElement.style.overflow}|${document.body?.style.overflow || ''}`;
    const now = () => window.performance?.now?.() ?? Date.now();
    const on = (target, type, handler, options) => {
      if (!target?.addEventListener) return;
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };
    const viewport = () => {
      const vv = window.visualViewport;
      const top = vv?.offsetTop || 0, left = vv?.offsetLeft || 0;
      const width = vv?.width || window.innerWidth, height = vv?.height || window.innerHeight;
      return { top, left, width, height, bottom: top + height };
    };
    const headerBottom = vp => Math.max(vp.top, ...headers.map(el => {
      const rect = el.getBoundingClientRect(), css = window.getComputedStyle(el);
      return !el.hidden && !el.hasAttribute('inert') && rect.width > 0 && rect.height > 0 && css.display !== 'none' && css.visibility !== 'hidden' && css.opacity !== '0' && (css.position === 'sticky' || css.position === 'fixed') && rect.top <= vp.top + 4 && rect.bottom > vp.top ? rect.bottom : vp.top;
    }));
    const overlayOpen = () => {
      if (host.hasAttribute('data-photo-editing')) return true;
      if (document.documentElement.style.overflow === 'hidden' || document.body?.style.overflow === 'hidden') return true;
      return [document, root].some(scope => [...scope.querySelectorAll(OVERLAY)].some(visible));
    };
    function hide() {
      attr(bar, 'data-commit-visible', 'false');
      if (!bar.hidden) bar.hidden = true;
      if (!bar.inert) bar.inert = true;
      attr(bar, 'aria-hidden', 'true');
    }
    function update() {
      frame = 0;
      if (destroyed) return;
      // Browser intersection delivery already accounts for scrolling and layout
      // shifts. Once confirmed offscreen, unrelated image loads/body resizes must
      // not force layout just to rediscover that the shortcut cannot be shown.
      if (pickerIntersects === false) { hide(); return; }
      const vp = viewport(), box = picker.getBoundingClientRect();
      // There cannot be a picker shortcut at the hero or past the picker. Avoid
      // measuring payment controls, headers, and dialogs in those common states.
      if (box.top >= vp.bottom || box.bottom <= vp.top) { hide(); return; }
      const top = headerBottom(vp);
      const mobile = (root.querySelector('#preview-surface')?.clientWidth || host.clientWidth || vp.width) <= 700;
      const anchorOffset = `${Math.ceil(top - vp.top + GAP)}px`;
      if (document.documentElement.style.getPropertyValue('--steadi-picker-anchor-offset') !== anchorOffset) document.documentElement.style.setProperty('--steadi-picker-anchor-offset', anchorOffset);
      reachedChoices ||= choices.getBoundingClientRect().top < vp.bottom;
      const purchaseNodes = [primary, host.querySelector('#steadi-express')].filter(visible);
      const purchaseBottom = purchaseNodes.length ? Math.max(...purchaseNodes.map(el => el.getBoundingClientRect().bottom)) : Infinity;
      const focused = root.activeElement || document.activeElement;
      const keyboard = editable(focused) && (mobile || vp.height < window.innerHeight - 120);
      const bottom = vp.bottom - (mobile ? 0 : GAP);
      if (!reachedChoices || purchaseBottom > top || box.bottom < bottom + GAP || bottom - barHeight < Math.max(top, box.top) + GAP || keyboard || overlayOpen()) {
        hide();
        return;
      }
      const width = mobile ? Math.min(vp.width, host.getBoundingClientRect().width) : Math.min(530, vp.width - 40, Math.max(400, box.width * .48 - 40));
      const left = mobile ? Math.max(vp.left, host.getBoundingClientRect().left) : Math.max(vp.left + 20, box.left + 32);
      const nextPosition = `${left}|${width}|${Math.max(0, window.innerHeight - bottom)}|${mobile}`;
      if (position !== nextPosition) {
        position = nextPosition;
        bar.style.setProperty('--steadi-commit-left', `${Math.round(left)}px`);
        bar.style.setProperty('--steadi-commit-width', `${Math.round(width)}px`);
        bar.style.setProperty('--steadi-commit-bottom', `${Math.round(Math.max(0, window.innerHeight - bottom))}px`);
      }
      attr(bar, 'data-commit-layout', mobile ? 'mobile' : 'desktop');
      attr(bar, 'data-commit-visible', 'true');
      if (bar.hidden) bar.hidden = false;
      if (bar.inert) bar.inert = false;
      attr(bar, 'aria-hidden', 'false');
      const measured = bar.getBoundingClientRect().height;
      if (measured > 0 && Math.abs(measured - barHeight) > 1) {
        barHeight = measured;
        refresh();
      }
    }
    function refresh() { if (!destroyed && !frame) frame = window.requestAnimationFrame(update); }
    function cancelSettle() {
      settleUntil = 0;
      window.clearTimeout(settleTimer);
      settleTimer = 0;
      if (pendingFrame) window.cancelAnimationFrame(pendingFrame);
      pendingFrame = 0;
    }
    function alignOnce(behavior) {
      if (destroyed) return;
      const vp = viewport(), offset = headerBottom(vp) + GAP;
      const delta = heading.getBoundingClientRect().top - offset;
      if (Math.abs(delta) > 2) window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior });
      refresh();
    }
    function settle() {
      window.clearTimeout(settleTimer);
      if (!settleUntil || now() >= settleUntil || destroyed) return;
      const delay = Math.max(60, settleAfter - now());
      settleTimer = window.setTimeout(() => {
        settleTimer = 0;
        if (settleUntil && now() < settleUntil) alignOnce('instant');
      }, delay);
    }
    function align({ smooth = true } = {}) {
      cancelSettle();
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      settleUntil = now() + 3200;
      settleAfter = now() + (smooth && !reduced ? 500 : 0);
      pendingFrame = window.requestAnimationFrame(() => {
        pendingFrame = 0;
        alignOnce(smooth && !reduced ? 'smooth' : 'instant');
        settle();
      });
    }
    function samePagePicker(link) {
      if (!link?.href || link.hasAttribute('download') || (link.target && link.target !== '_self')) return false;
      try {
        const url = new URL(link.href, window.location.href);
        return url.origin === window.location.origin && url.pathname === window.location.pathname && url.search === window.location.search && url.hash === '#picker';
      } catch { return false; }
    }
    function anchorClick(event) {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const path = event.composedPath?.() || [];
      const link = path.find(el => el?.matches?.('a[href]')) || event.target?.closest?.('a[href]');
      if (!samePagePicker(link)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (window.location.hash !== '#picker') window.history.pushState(null, '', '#picker');
      if(link.hasAttribute('data-se-launch-personalize'))window.dispatchEvent(new CustomEvent('steadi:personalize-request'));
      align();
    }
    const layoutChanged = () => { refresh(); settle(); };
    on(window, 'scroll', refresh, { passive: true });
    on(window, 'resize', layoutChanged, { passive: true });
    on(window.visualViewport, 'resize', layoutChanged, { passive: true });
    on(window.visualViewport, 'scroll', refresh, { passive: true });
    on(document, 'click', anchorClick, true);
    on(document, 'load', layoutChanged, true);
    on(document, 'focusin', refresh, true);
    on(document, 'focusout', refresh, true);
    on(root, 'focusin', refresh, true);
    on(root, 'focusout', refresh, true);
    on(root, 'steadi:rendered', layoutChanged, true);
    on(window, 'wheel', cancelSettle, { passive: true });
    on(window, 'touchstart', cancelSettle, { passive: true });
    on(window, 'pointerdown', cancelSettle, { passive: true });
    on(window, 'keydown', event => { if (SCROLL_KEYS.has(event.key)) cancelSettle(); });
    on(window, 'hashchange', () => { if (window.location.hash === '#picker') align({ smooth: false }); else cancelSettle(); });
    if (window.ResizeObserver) {
      const observer = new window.ResizeObserver(layoutChanged);
      [picker, heading, choices, primary, document.body, ...document.querySelectorAll(HEADER)].filter(Boolean).forEach(el => observer.observe(el));
      cleanups.push(() => observer.disconnect());
    }
    if (window.IntersectionObserver) {
      const observer = new window.IntersectionObserver(entries => {
        for (const entry of entries || []) if (entry.target === picker) pickerIntersects = entry.isIntersecting;
        // Always refresh: an offscreen cache can be stale until this callback
        // follows a scroll or anchor jump into the picker.
        refresh();
      }, { threshold: [0, 1] });
      [picker, primary, choices].forEach(el => observer.observe(el));
      cleanups.push(() => observer.disconnect());
    }
    if (window.MutationObserver) {
      // Observe state, not animation. The old unfiltered observer turned every
      // app/pixel text update and our own fixed-bar/style writes into another
      // full layout measurement. Picker size changes already have ResizeObserver
      // and the rendered event; only actual overlay/header/payment state needs
      // a mutation-triggered measurement.
      const contains = (parent, child) => parent === child || !!parent?.contains?.(child);
      const hasRelevantNode = node => !!node?.matches?.(`${HEADER}, ${OVERLAY_CANDIDATE}, #steadi-express`) || !!node?.querySelector?.(`${HEADER}, ${OVERLAY_CANDIDATE}, #steadi-express`);
      const observer = new window.MutationObserver(records => {
        let changed = false, structure = false;
        const payment = host.querySelector('#steadi-express');
        for (const record of records) {
          const target = record.target;
          // These writes are output of this controller, never layout inputs.
          if (contains(bar, target) || target === style) continue;
          if (record.type === 'childList') {
            if ([...record.addedNodes, ...record.removedNodes].some(hasRelevantNode)) structure = changed = true;
            continue;
          }
          if (target === document.documentElement || target === document.body) {
            const nextLock = `${document.documentElement.style.overflow}|${document.body?.style.overflow || ''}`;
            if (nextLock !== scrollLock) { scrollLock = nextLock; changed = true; }
            // Header visibility can also be controlled by a body class.
            if (record.attributeName === 'class' || record.attributeName === 'hidden') changed = true;
            continue;
          }
          if (target === host) { if (record.attributeName === 'data-photo-editing' || record.attributeName === 'hidden') changed = true; continue; }
          if (record.attributeName === 'role' || record.attributeName === 'aria-modal') structure = changed = true;
          if (record.attributeName === 'class' && target?.matches?.(HEADER)) structure = changed = true;
          if (target === primary || contains(target, payment) || headers.some(el => contains(target, el)) || overlays.some(el => contains(target, el))) changed = true;
        }
        if (structure) { headers = [...document.querySelectorAll(HEADER)]; collectOverlays(); }
        if (changed) layoutChanged();
      });
      observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'open', 'aria-hidden', 'aria-modal', 'role', 'style', 'class'] });
      observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'open', 'aria-hidden', 'aria-modal', 'role', 'style', 'class', 'data-photo-editing'] });
      cleanups.push(() => observer.disconnect());
    }
    document.fonts?.ready?.then(() => { if (!destroyed) layoutChanged(); });
    const api = { refresh, align, destroy() {
      destroyed = true;
      cancelSettle();
      if (frame) window.cancelAnimationFrame(frame);
      cleanups.forEach(cleanup => cleanup());
      hide();
      style.remove();
      if (originalParent) originalParent.insertBefore(bar, originalNext?.parentNode === originalParent ? originalNext : null);
      host.removeAttribute('data-picker-navigation');
      mounted.delete(host);
    } };
    mounted.set(host, api);
    hide();
    refresh();
    if (window.location.hash === '#picker') align({ smooth: false });
    return api;
  }
  window.SteadiPickerNavigation = { mount, version: '1' };
})();

